<script setup lang="ts">
import { formatCepMasked, normalizeCepDigits } from '@solfacil/frontend-utils/cep';
import { formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils/cpf';
import { computed } from 'vue';

import Divider from '~/components/shared/Divider.vue';
import DocumentViewer from '~/components/shared/DocumentViewer.vue';
import InfoGridItem from '~/components/shared/InfoGridItem.vue';

import useCustomerRegistrationDetails from '../../queries/useCustomerRegistrationDetails';

interface Props {
  isLoading?: boolean;
  projectId: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const registrationQuery = useCustomerRegistrationDetails(computed(() => props.projectId));
const registrationData = computed(() => registrationQuery.data.value);
const internalIsLoading = computed(
  () => props.isLoading || registrationQuery.isLoading.value || registrationQuery.isFetching.value,
);

const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) return null;
  return value ? 'Sim' : 'Não';
};

const installationAddressData = computed(() => {
  const address = registrationData.value?.project?.address;
  if (!address) return [];

  return [
    { label: 'CEP', value: address.zipcode ? formatCepMasked(normalizeCepDigits(address.zipcode)) : null },
    { label: 'Rua', value: address.street || null },
    { label: 'Número', value: address.street_number || null },
    { label: 'Complemento', value: address.complement || null },
    { label: 'Bairro', value: address.neighborhood || null },
    { label: 'Cidade', value: address.city?.name || null },
    { label: 'Estado', value: address.state?.name || null },
    { label: 'Zona Rural', value: formatBoolean(address.is_rural) },
  ].filter((item) => item.value !== null);
});

const electricityBillIsUnderCustomerName = computed(() => {
  const project = registrationData.value?.project;
  if (!project?.electricity_bill) return null;
  return formatBoolean(project.electricity_bill.is_under_customer_name);
});

const installationElectricityBillDocuments = computed(() => {
  const documents = registrationData.value?.project?.documents || [];
  return documents.filter((doc) => doc.type === 'electricity_bill');
});

const additionalDocuments = computed(() => {
  const documents = registrationData.value?.project?.documents || [];
  return documents.filter((doc) => doc.type === 'additional');
});

const relationshipProofDocuments = computed(() => {
  const project = registrationData.value?.project;
  if (!project?.electricity_bill || project.electricity_bill.is_under_customer_name) {
    return [];
  }

  const documents = project.documents || [];
  return documents.filter((doc) => doc.type === 'employment_proof');
});

const relationshipProofHolderCpf = computed(() => {
  const project = registrationData.value?.project;
  if (!project?.electricity_bill || project.electricity_bill.is_under_customer_name) {
    return null;
  }

  return project.electricity_bill.holder_cpf
    ? formatCpfMasked(normalizeCpfDigits(project.electricity_bill.holder_cpf)) : null;
});

const projectComment = computed(() => registrationData.value?.project?.comment || null);

const hasData = computed(() => (
  projectComment.value !== null
  || installationAddressData.value.length > 0
  || electricityBillIsUnderCustomerName.value !== null
  || installationElectricityBillDocuments.value.length > 0
  || relationshipProofDocuments.value.length > 0
  || additionalDocuments.value.length > 0
));

const skeletonInstallationAddressData = computed(() => {
  if (!internalIsLoading.value) return [];
  return [
    { label: 'CEP', value: null },
    { label: 'Rua', value: null },
    { label: 'Número', value: null },
    { label: 'Complemento', value: null },
    { label: 'Bairro', value: null },
    { label: 'Cidade', value: null },
    { label: 'Estado', value: null },
    { label: 'Zona Rural', value: null },
  ];
});
</script>

<template>
  <div class="project-data-content">
    <div v-if="!internalIsLoading && !hasData" class="empty-state">
      <p class="empty-message">Dados ainda não preenchidos</p>
    </div>

    <template v-else>
      <div class="datagrid">
        <div
          v-if="(internalIsLoading ? skeletonInstallationAddressData : installationAddressData).length > 0"
          class="section-title-wrapper"
        >
          <h5 class="section-title">Endereço de Instalação</h5>
          <Divider />
        </div>

        <InfoGridItem
          v-for="item in (internalIsLoading ? skeletonInstallationAddressData : installationAddressData)"
          :key="item.label"
          :label="item.label"
          :value="item.value"
          :is-loading="internalIsLoading"
        />
      </div>

      <div
        v-if="
          internalIsLoading
            || installationElectricityBillDocuments.length > 0
            || electricityBillIsUnderCustomerName !== null
            || relationshipProofDocuments.length > 0
            || additionalDocuments.length > 0
            || projectComment
        "
        class="documents-row"
      >
        <div
          v-if="
            internalIsLoading
              || installationElectricityBillDocuments.length > 0
              || electricityBillIsUnderCustomerName !== null
          "
          class="documents-section"
        >
          <div class="section-title-wrapper">
            <h5 class="section-title">Conta de Luz do Local de Instalação</h5>
            <Divider />
          </div>
          <div v-if="internalIsLoading || electricityBillIsUnderCustomerName !== null" class="datagrid">
            <InfoGridItem
              label="Conta está no nome do cliente"
              :value="electricityBillIsUnderCustomerName"
              :is-loading="internalIsLoading"
            />
          </div>
          <div class="documents-list">
            <DocumentViewer
              v-for="(doc, index) in (internalIsLoading ? [undefined] : installationElectricityBillDocuments)"
              :key="internalIsLoading ? `skeleton-${index}` : doc?.id"
              :document="doc"
              :is-loading="internalIsLoading"
            />
          </div>
        </div>

        <div v-if="internalIsLoading || relationshipProofDocuments.length > 0" class="documents-section">
          <div class="section-title-wrapper">
            <h5 class="section-title">Comprovante de Vínculo</h5>
            <Divider />
          </div>
          <div class="relationship-proof-content">
            <div class="documents-list">
              <DocumentViewer
                v-for="(doc, index) in (internalIsLoading ? [undefined] : relationshipProofDocuments)"
                :key="internalIsLoading ? `skeleton-${index}` : doc?.id"
                :document="doc"
                :is-loading="internalIsLoading"
              />
            </div>
            <div v-if="relationshipProofHolderCpf" class="holder-cpf-section">
              <InfoGridItem
                label="CPF do Titular"
                :value="relationshipProofHolderCpf"
                :is-loading="internalIsLoading"
              />
            </div>
          </div>
        </div>

        <div
          v-if="internalIsLoading || additionalDocuments.length > 0 || projectComment"
          class="documents-section combined-section"
        >
          <div class="combined-section-header">
            <div v-if="internalIsLoading || additionalDocuments.length > 0" class="section-title-wrapper">
              <h5 class="section-title">Documentos Adicionais</h5>
              <Divider />
            </div>
          </div>
          <div class="combined-section-content">
            <div v-if="internalIsLoading || additionalDocuments.length > 0" class="additional-documents-section">
              <div class="documents-list">
                <DocumentViewer
                  v-for="(doc, index) in (internalIsLoading ? [undefined] : additionalDocuments)"
                  :key="internalIsLoading ? `skeleton-${index}` : doc?.id"
                  :document="doc"
                  :is-loading="internalIsLoading"
                />
              </div>
            </div>
            <div v-if="projectComment" class="comment-section">
              <InfoGridItem
                label="Comentário"
                :value="projectComment"
                :is-loading="internalIsLoading"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.project-data-content {
  .datagrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 0.75rem;
  }

  .section-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.5rem 0 0.75rem;
    grid-column: 1 / -1;
    width: 100%;
    min-width: 0;

    &:first-child {
      margin-top: 0;
    }

    :deep(.divider) {
      flex: 1;
      min-width: 0;
      width: auto;
    }
  }

  .additional-documents-section .section-title-wrapper {
    margin-top: 0;
  }

  .section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }

  .documents-row {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }

  .documents-section {
    width: 100%;

    &.combined-section {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
  }

  .combined-section-header {
    width: 100%;

    .section-title-wrapper {
      margin: 0;
    }
  }

  .combined-section-content {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
    width: 100%;
  }

  .relationship-proof-content {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
    margin-top: 0.75rem;

    .documents-list {
      margin-top: 0;
      flex: 1;
      min-width: 0;
    }
  }

  .holder-cpf-section {
    flex: 0 0 auto;
    min-width: 250px;
    display: flex;
    align-items: flex-start;
  }

  .comment-section {
    flex: 0 0 auto;
    min-width: 250px;
    display: flex;
    align-items: flex-start;
  }

  .additional-documents-section {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .documents-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
  }

  .empty-message {
    color: #94a3b8;
    font-size: 0.9375rem;
    font-style: italic;
    margin: 0;
  }
}
</style>
